import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { MapPin, Mail, Phone, Send } from 'lucide-react';
import { mockQuoteSubmit, mockContactSubmit } from '../utils/mockData';
import { toast } from 'sonner';

const ContactSection = () => {
  const [quoteForm, setQuoteForm] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    productInterest: 'Rutile',
    quantity: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleQuoteChange = (e) => {
    setQuoteForm({
      ...quoteForm,
      [e.target.name]: e.target.value
    });
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    const result = await mockQuoteSubmit(quoteForm);
    
    if (result.success) {
      toast.success(result.message);
      setQuoteForm({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        productInterest: 'Rutile',
        quantity: '',
        message: ''
      });
    } else {
      toast.error(result.message || 'Failed to submit quote. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-bharat-blue rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-manufacturing-orange/10 text-manufacturing-orange px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Get in Touch
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-bharat-blue mb-6">
            Looking for a Reliable TiO₂ Supply Partner?
          </h2>
          <p className="text-xl text-steel-grey max-w-3xl mx-auto">
            Our team is ready to support your manufacturing needs
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Contact Info */}
          <div>
            {/* Company Card */}
            <div className="bg-gradient-to-br from-bharat-blue to-bharat-blue/90 rounded-2xl p-8 text-white mb-8 shadow-xl">
              <img 
                src="/logo-secondary-white.png" 
                alt="Bharat Enterprises"
                className="h-16 mb-6"
              />
              <h3 className="text-2xl font-bold mb-4">Bharat Enterprises</h3>
              <p className="text-white/90 mb-6 text-lg">
                Built on Quality. Delivered with Trust.
              </p>
              
              {/* Contact Details */}
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Address</div>
                    <div className="text-white/80">
                      A-51, GT Karnal Road Industrial Area<br />
                      Opp. Hans Cinema, Azadpur Metro Station<br />
                      Delhi – 110033
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Email</div>
                    <a href="mailto:info@bharatenterprises.org" className="text-white/80 hover:text-white transition-colors">
                      info@bharatenterprises.org
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold mb-1">Phone</div>
                    <a href="tel:+919999996696" className="text-white/80 hover:text-white transition-colors">
                      +91 99999 96696
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <h4 className="font-bold text-bharat-blue mb-4">Business Hours</h4>
              <div className="space-y-2 text-steel-grey">
                <div className="flex justify-between">
                  <span>Monday - Saturday</span>
                  <span className="font-semibold">10:00 AM - 6:30 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="font-semibold text-manufacturing-orange">Closed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Quote Form */}
          <div>
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-gray-100">
              <h3 className="text-2xl font-bold text-bharat-blue mb-6">
                Request a Quote
              </h3>
              
              <form onSubmit={handleQuoteSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-steel-grey mb-2">
                      Company Name *
                    </label>
                    <Input
                      name="companyName"
                      value={quoteForm.companyName}
                      onChange={handleQuoteChange}
                      required
                      placeholder="Your company name"
                      className="border-gray-300 focus:border-manufacturing-orange"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-steel-grey mb-2">
                      Contact Person *
                    </label>
                    <Input
                      name="contactPerson"
                      value={quoteForm.contactPerson}
                      onChange={handleQuoteChange}
                      required
                      placeholder="Your name"
                      className="border-gray-300 focus:border-manufacturing-orange"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-steel-grey mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={quoteForm.email}
                      onChange={handleQuoteChange}
                      required
                      placeholder="your@email.com"
                      className="border-gray-300 focus:border-manufacturing-orange"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-steel-grey mb-2">
                      Phone *
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={quoteForm.phone}
                      onChange={handleQuoteChange}
                      required
                      placeholder="+91 XXXXX XXXXX"
                      className="border-gray-300 focus:border-manufacturing-orange"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-steel-grey mb-2">
                      Product Interest *
                    </label>
                    <select
                      name="productInterest"
                      value={quoteForm.productInterest}
                      onChange={handleQuoteChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-manufacturing-orange"
                    >
                      <option value="Rutile">Rutile Titanium Dioxide</option>
                      <option value="Anatase">Anatase Titanium Dioxide</option>
                      <option value="Both">Both Grades</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-steel-grey mb-2">
                      Estimated Quantity (MT)
                    </label>
                    <Input
                      name="quantity"
                      value={quoteForm.quantity}
                      onChange={handleQuoteChange}
                      placeholder="e.g., 10 MT"
                      className="border-gray-300 focus:border-manufacturing-orange"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-steel-grey mb-2">
                    Additional Requirements
                  </label>
                  <Textarea
                    name="message"
                    value={quoteForm.message}
                    onChange={handleQuoteChange}
                    rows={4}
                    placeholder="Tell us about your specific requirements, delivery timeline, or any other details..."
                    className="border-gray-300 focus:border-manufacturing-orange"
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-manufacturing-orange hover:bg-manufacturing-orange/90 text-white text-lg py-6"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                  <Send className="ml-2" />
                </Button>

                <p className="text-sm text-steel-grey text-center">
                  We typically respond within 24 hours during business days
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
